<template>
  <div v-if="!isLoading && openOrders.length > 0">
    <div class="mel-divider d-flex align-items-center my-3">
      <div class="text-primary mx-3">
        <font-awesome-icon :icon="['fas', 'cart-shopping']" class="me-1" />
        {{ $t('page.item.openOrders.title') }}
        <span class="badge bg-secondary ms-1">{{ openOrders.length }}</span>
      </div>
    </div>

    <table class="table table-borderless table-sm align-middle mb-0">
      <thead>
        <tr>
          <th>{{ $t('field.generic.order') }}</th>
          <th>{{ $t('field.generic.status') }}</th>
          <th>{{ $t('field.generic.supplier') }}</th>
          <th>{{ $t('field.order.deliveryDate') }}</th>
          <th class="text-end">{{ $t('page.item.openOrders.openQuantity') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in openOrders" :key="`${entry.orderId}-${entry.line.lineItemId}`">
          <td>
            <RouterLink :to="{ name: 'order', params: { orderId: entry.orderId } }" class="btn btn-link text-start ps-0 py-0">
              {{ entry.orderCode }}
            </RouterLink>
            <span v-if="entry.line.isReplacement" class="badge bg-warning text-dark ms-1" :title="$t('page.item.openOrders.replacementOf', { code: entry.line.lineItemCode, name: entry.line.lineItemName })">
              <font-awesome-icon :icon="['fas', 'right-left']" fixed-width />
              {{ entry.line.lineItemCode }}
            </span>
          </td>
          <td>
            <OrderStatus :status="entry.status" />
          </td>
          <td class="text-melodi">{{ entry.supplierName }}</td>
          <td>
            <span v-if="entry.deliveryDate">{{ $d(new Date(entry.deliveryDate), 'dateonly') }}</span>
          </td>
          <td class="text-end">
            <span class="fw-semibold">{{ entry.line.openQuantity }}</span>
            <span class="text-muted"> / {{ entry.line.quantity }} {{ entry.line.unitName }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useItemsStore } from "@/stores";
import OrderStatus from "@/components/orders/OrderStatus.vue";
import type { IItemOpenOrder } from "@/models/responses/IItemOpenOrder";

const props = defineProps<{
  itemId: number;
}>();

const itemsStore = useItemsStore();
const openOrders = ref<IItemOpenOrder[]>([]);
const isLoading = ref(false);

const load = async () => {
  if (!props.itemId) {
    openOrders.value = [];
    return;
  }
  isLoading.value = true;
  try {
    openOrders.value = await itemsStore.fetchOpenOrders(props.itemId);
  } catch (error) {
    console.error("Failed to load open orders for item", error);
    openOrders.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(load);
watch(() => props.itemId, load);
</script>
